/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import Text from '@Text';
import {
  groupSlotsByDay,
  getSlotTimeLabel,
  isSlotAvailable,
  getSlotDayKey,
  getSlotDayTileParts,
  getSlotDateHeadingLabel,
  getDayRangeLabel,
} from './slotDateTime.helpers';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import makeSlotPickerStyles, { ITEM_GAP } from './makeSlotPickerStyles';
import useClientApiCopy from './copy/useClientApiCopy';
import usePagedTrack from './usePagedTrack';
import { PagerArrows, PagerDots } from './PagerControls';

// Days are shown a few at a time; a wider screen fits more per page rather than
// stretching each tile.
const DAYS_PER_PAGE_MOBILE = 4;
const DAYS_PER_PAGE_TABLET = 4;
const DAYS_PER_PAGE_DESKTOP = 5;
// Times stay pills but page the same way. Fewer per page on a phone, where the
// row is narrow and five pills leave HH:MM cramped.
const TIMES_PER_PAGE_MOBILE = 3;
const TIMES_PER_PAGE_WIDE = 5;

const chunk = (items, size) => {
  const pages = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages;
};

const fadeClass = (classes, edgeCut) => [
  classes.pagerTrack,
  edgeCut.left && edgeCut.right && classes.pagerTrackFadeBoth,
  edgeCut.left && !edgeCut.right && classes.pagerTrackFadeLeft,
  !edgeCut.left && edgeCut.right && classes.pagerTrackFadeRight,
];

// An equal share of the page: n items with ITEM_GAP between them, so each is
// that fraction of the page width. The peek item gets the same share.
const itemBasis = (perPage) =>
  `calc((100% - ${(perPage - 1) * ITEM_GAP}px) / ${perPage})`;

const SlotGrid = ({ locationId, slots, selectedSlot, onSelectSlot }) => {
  const theme = useTheme();
  const classes = makeSlotPickerStyles({ theme });
  const copy = useClientApiCopy();
  const isTablet = useMediaQuery(theme.breakpoints.up('tabletSmall'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktopSmall'));
  // eslint-disable-next-line no-nested-ternary
  const daysPerPage = isDesktop
    ? DAYS_PER_PAGE_DESKTOP
    : isTablet
    ? DAYS_PER_PAGE_TABLET
    : DAYS_PER_PAGE_MOBILE;
  const timesPerPage = isTablet ? TIMES_PER_PAGE_WIDE : TIMES_PER_PAGE_MOBILE;
  const localeCode = useSelector((store) => store.formConfig?.localeCode);

  const days = groupSlotsByDay((slots ?? []).filter(isSlotAvailable));
  // Open on the chosen slot's day when returning from Review (the slot survives
  // Back), so the shown day matches the slot that keeps Continue enabled.
  const selectedDayKey =
    selectedSlot?.locationId === locationId && selectedSlot?.start
      ? getSlotDayKey(selectedSlot.start)
      : undefined;
  const [activeDayKey, setActiveDayKey] = useState(
    selectedDayKey ?? days[0]?.dayKey
  );

  const dayPages = chunk(days, daysPerPage);
  const activeDayIndex = Math.max(
    0,
    days.findIndex((day) => day.dayKey === activeDayKey)
  );

  const dayTrack = usePagedTrack({
    itemCount: days.length,
    pageSize: daysPerPage,
    activeIndex: activeDayIndex,
  });

  useWidgetHeightRefresh([activeDayKey]);

  if (days.length === 0) {
    return (
      <Box css={classes.message} data-testid="noSlots">
        <Text variant="text">{copy.slot.noSlots}</Text>
      </Box>
    );
  }

  const activeDay = days.find((day) => day.dayKey === activeDayKey) ?? days[0];
  const activeDaySlots = activeDay?.slots ?? [];

  const isSelected = (slot) =>
    selectedSlot?.locationId === locationId &&
    selectedSlot?.start === slot.start;

  const tileBasis = itemBasis(daysPerPage);

  // Selecting a day also brings its page fully into view, so a part-hidden peek
  // tile is never left clipped — clicking it pages to where it sits in full.
  // Moving to a different day than the selected slot clears that slot, so Continue
  // is only enabled when the day on screen has a time picked; a time from a day the
  // user has navigated away from must not keep Continue active.
  const selectDay = (day, dayIndex) => {
    if (selectedSlot && day.dayKey !== selectedDayKey) {
      onSelectSlot(null);
    }
    setActiveDayKey(day.dayKey);
    dayTrack.goToIndexPage(dayIndex);
  };

  const renderDayTile = (day, dayIndex) => {
    const isActive = day.dayKey === activeDay?.dayKey;
    const parts = getSlotDayTileParts(day.dayKey, localeCode);
    return (
      <MuiButton
        key={day.dayKey}
        css={[classes.dayTile, isActive && classes.dayTileSelected]}
        style={{ flex: `0 0 ${tileBasis}` }}
        onClick={() => selectDay(day, dayIndex)}
        data-testid={`datePill-${day.dayKey}`}
        data-pager-item
        aria-pressed={isActive}
        aria-label={getSlotDateHeadingLabel(day.slots[0].start, localeCode)}
      >
        <span css={classes.dayTileWeekday}>{parts.weekday}</span>
        <span css={classes.dayTileNum} data-tile-part="num">
          {parts.dayOfMonth}
        </span>
        <span css={classes.dayTileMonth}>{parts.month}</span>
      </MuiButton>
    );
  };

  const daysPaged = dayPages.length > 1;
  const dayPageDays = dayPages[dayTrack.currentPage] ?? [];
  const rangeLabel = getDayRangeLabel(
    dayPageDays[0]?.dayKey,
    dayPageDays[dayPageDays.length - 1]?.dayKey,
    localeCode
  );

  return (
    <Box>
      {!daysPaged && (
        <Box css={classes.dayTileRow}>{days.map(renderDayTile)}</Box>
      )}
      {daysPaged && (
        <Box data-testid="slotDayPager">
          <Box css={classes.pagerTop}>
            <Box css={classes.pagerRange} data-testid="slotPagerRange">
              <Text variant="text">{rangeLabel}</Text>
            </Box>
            <PagerArrows
              classes={classes}
              currentPage={dayTrack.currentPage}
              pageCount={dayTrack.pageCount}
              goToPage={dayTrack.goToPage}
              previousLabel={copy.slot.previousDays}
              nextLabel={copy.slot.nextDays}
              testIdPrefix="slotPager"
            />
          </Box>
          <Box
            css={fadeClass(classes, dayTrack.edgeCut)}
            ref={dayTrack.trackRef}
            onScroll={dayTrack.onTrackScroll}
            data-testid="slotPagerTrack"
          >
            {dayPages.map((daysOnPage, pageIndex) => (
              <Box key={daysOnPage[0].dayKey} css={classes.pagerPage}>
                {daysOnPage.map((day, indexOnPage) =>
                  renderDayTile(day, pageIndex * daysPerPage + indexOnPage)
                )}
              </Box>
            ))}
          </Box>
          <PagerDots
            classes={classes}
            currentPage={dayTrack.currentPage}
            pageKeys={dayPages.map((daysOnPage) => daysOnPage[0].dayKey)}
            testIdPrefix="slotPager"
          />
        </Box>
      )}
      <TimeRow
        key={activeDay.dayKey}
        classes={classes}
        copy={copy}
        localeCode={localeCode}
        locationId={locationId}
        activeDay={activeDay}
        activeDaySlots={activeDaySlots}
        timesPerPage={timesPerPage}
        isSelected={isSelected}
        onSelectSlot={onSelectSlot}
      />
    </Box>
  );
};

// The time row: pills that page exactly like the day row, with the arrows on the
// date-heading row and no range label. Remounted per day (key on the day) so its
// page state resets; it seeds to the page holding this day's chosen time.
const TimeRow = ({
  classes,
  copy,
  localeCode,
  locationId,
  activeDay,
  activeDaySlots,
  timesPerPage,
  isSelected,
  onSelectSlot,
}) => {
  const selectedTimeIndex = activeDaySlots.findIndex(isSelected);
  const timeTrack = usePagedTrack({
    itemCount: activeDaySlots.length,
    pageSize: timesPerPage,
    activeIndex: selectedTimeIndex < 0 ? 0 : selectedTimeIndex,
  });

  const timesPaged = timeTrack.pageCount > 1;
  const timePages = chunk(activeDaySlots, timesPerPage);
  const pillBasis = itemBasis(timesPerPage);

  const renderTimePill = (slot, slotIndex) => (
    <MuiButton
      key={slot.start}
      css={[classes.timePill, isSelected(slot) && classes.pillActive]}
      style={{ flex: `0 0 ${pillBasis}` }}
      onClick={() => {
        if (isSelected(slot)) {
          onSelectSlot(null);
          return;
        }
        onSelectSlot({ locationId, start: slot.start, end: slot.end });
        timeTrack.goToIndexPage(slotIndex);
      }}
      data-testid={`slotOption-${slot.start}`}
      data-pager-item
      aria-pressed={isSelected(slot)}
    >
      {getSlotTimeLabel(slot.start)}
    </MuiButton>
  );

  return (
    <Box>
      <Box css={classes.timesHead}>
        <Box css={classes.sectionLabel}>
          <Text variant="text">
            {getSlotDateHeadingLabel(activeDay.slots[0].start, localeCode)}
          </Text>
        </Box>
        {timesPaged && (
          <PagerArrows
            classes={classes}
            currentPage={timeTrack.currentPage}
            pageCount={timeTrack.pageCount}
            goToPage={timeTrack.goToPage}
            previousLabel={copy.slot.previousTimes}
            nextLabel={copy.slot.nextTimes}
            testIdPrefix="slotTimePager"
          />
        )}
      </Box>
      {!timesPaged && (
        <Box css={classes.pillRow}>{activeDaySlots.map(renderTimePill)}</Box>
      )}
      {timesPaged && (
        <Box data-testid="slotTimeDayPager">
          <Box
            css={fadeClass(classes, timeTrack.edgeCut)}
            ref={timeTrack.trackRef}
            onScroll={timeTrack.onTrackScroll}
            data-testid="slotTimePagerTrack"
          >
            {timePages.map((slotsOnPage, pageIndex) => (
              <Box key={slotsOnPage[0].start} css={classes.pagerPage}>
                {slotsOnPage.map((slot, indexOnPage) =>
                  renderTimePill(slot, pageIndex * timesPerPage + indexOnPage)
                )}
              </Box>
            ))}
          </Box>
          <PagerDots
            classes={classes}
            currentPage={timeTrack.currentPage}
            pageKeys={timePages.map((slotsOnPage) => slotsOnPage[0].start)}
            testIdPrefix="slotTimePager"
          />
        </Box>
      )}
    </Box>
  );
};

export default SlotGrid;
